// 16-PPH-EVIDENCE-R1 (2026-06-14, user request): Dialog pencatatan pembayaran
// PPh ke negara — mendukung SATU atau BEBERAPA invoice dalam satu pembayaran
// pajak (NTPN/bukti setor sama). Preview daftar invoice + total PPh tercakup
// sebelum simpan. Alokasi per invoice divalidasi ≤ sisa terutang.
// Mode edit (koreksi) bila prop `payment` diisi — hanya user berwenang.
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Paperclip, Landmark } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { ACTION_LABEL } from "@/lib/i18nLabels";

export default function PphPaymentDialog({ items, banks, methods, payment, onClose, onSaved }) {
  const isEdit = !!payment;
  const [paymentDate, setPaymentDate] = useState(payment?.payment_date || new Date().toISOString().slice(0, 10));
  const [ntpn, setNtpn] = useState(payment?.ntpn || "");
  const [proofNumber, setProofNumber] = useState(payment?.proof_number || "");
  const [method, setMethod] = useState(payment?.method || "Transfer Bank");
  const [bank, setBank] = useState(payment?.bank || "");
  const [note, setNote] = useState(payment?.note || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allocs, setAllocs] = useState(() => {
    const m = {};
    if (isEdit) {
      for (const a of payment.allocations || []) m[`${a.project_id}|${a.entry_id}`] = String(a.amount);
    } else {
      for (const i of items) m[`${i.project_id}|${i.entry_id}`] = String(i.pph_remaining);
    }
    return m;
  });

  const rows = useMemo(() => (isEdit ? (payment.allocations || []) : items), [isEdit, payment, items]);
  const total = useMemo(
    () => rows.reduce((s, i) => s + (parseFloat(allocs[`${i.project_id}|${i.entry_id}`]) || 0), 0),
    [rows, allocs],
  );

  const invalid = useMemo(() => rows.some((i) => {
    const v = parseFloat(allocs[`${i.project_id}|${i.entry_id}`]) || 0;
    if (v <= 0) return true;
    if (!isEdit && v - i.pph_remaining > 0.005) return true;
    return false;
  }), [rows, allocs, isEdit]);

  const save = async () => {
    if (!paymentDate) { toast.error("Tanggal pembayaran wajib diisi."); return; }
    if (invalid) { toast.error("Periksa alokasi: harus > 0 dan tidak melebihi sisa PPh terutang."); return; }
    setBusy(true);
    try {
      const payload = {
        payment_date: paymentDate,
        ntpn: ntpn.trim() || null,
        proof_number: proofNumber.trim() || null,
        method: method || null,
        bank: bank || null,
        note: note.trim() || null,
        allocations: rows.map((i) => ({
          project_id: i.project_id,
          entry_id: i.entry_id,
          amount: parseFloat(allocs[`${i.project_id}|${i.entry_id}`]) || 0,
        })),
      };
      const res = isEdit
        ? await api.patch(`/finance/pph-payments/${payment.id}`, payload)
        : await api.post("/finance/pph-payments", payload);
      const payId = isEdit ? payment.id : res.data.id;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/finance/pph-payments/${payId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEdit
        ? "Koreksi pembayaran PPh tersimpan & tercatat di Audit Log."
        : "Pembayaran PPh tercatat & masuk Audit Log. Nilai invoice & pembukuan tidak berubah.");
      onSaved();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Gagal menyimpan pembayaran PPh.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white border border-slate-300 shadow-xl max-h-[92vh] overflow-y-auto"
        data-testid="pph-payment-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Landmark size={15} className="text-[#0B1D3A]" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Potongan PPh</div>
              <div className="text-sm font-bold text-[#0B1D3A]">{isEdit ? "Koreksi Pembayaran PPh ke Negara" : "Tandai PPh Sudah Dibayar ke Negara"}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="pph-pay-close" className="p-1 text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tanggal Pembayaran *</span>
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} data-testid="pph-pay-date"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nomor Referensi / NTPN</span>
              <input value={ntpn} onChange={(e) => setNtpn(e.target.value)} data-testid="pph-pay-ntpn" placeholder="mis. 0802xxxxxxxxxxxx"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">No Bukti Setor / Kode Billing</span>
              <input value={proofNumber} onChange={(e) => setProofNumber(e.target.value)} data-testid="pph-pay-proof" placeholder="mis. 411121-xxxxxxxx"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Metode Pembayaran</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pph-pay-method"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white">
                {(methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bank Tujuan / Sumber Pembayaran</span>
              <select value={bank} onChange={(e) => setBank(e.target.value)} data-testid="pph-pay-bank"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white">
                <option value="">— Pilih rekening perusahaan / lainnya —</option>
                {(banks || []).map((b) => (
                  <option key={b.id || b.account_no} value={`${b.bank_name || ""} — ${b.account_no || ""}`}>
                    {b.bank_name} — {b.account_no}{b.account_name ? ` (${b.account_name})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Catatan</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} data-testid="pph-pay-note"
                placeholder="Catatan opsional (mis. setor gabungan masa pajak Juni 2026)…"
                className="mt-0.5 w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
            </label>
            {!isEdit && (
              <label className="block sm:col-span-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Upload Bukti Pembayaran</span>
                <span className="mt-0.5 flex items-center gap-2 border border-dashed border-slate-300 px-2 py-2 text-[11px] text-slate-600">
                  <Paperclip size={13} />
                  <input type="file" accept="image/*,application/pdf" data-testid="pph-pay-file"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-[11px]" />
                </span>
                {file && <span className="text-[10px] text-slate-500 mt-0.5 block">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>}
              </label>
            )}
          </div>

          {/* Preview invoice tercakup */}
          <div className="border border-slate-200" data-testid="pph-pay-preview">
            <div className="px-2.5 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-semibold text-slate-600">
              Invoice Tercakup ({rows.length}) — periksa sebelum menyimpan
            </div>
            <table className="w-full text-[11px]">
              <thead className="text-[9px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-2.5 py-1.5 text-left">Invoice / Proyek</th>
                  <th className="px-2.5 py-1.5 text-right">Sisa Terutang</th>
                  <th className="px-2.5 py-1.5 text-right w-36">Alokasi Pembayaran</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => {
                  const key = `${i.project_id}|${i.entry_id}`;
                  const remaining = isEdit ? i.pph_remaining : i.pph_remaining;
                  const v = parseFloat(allocs[key]) || 0;
                  const bad = !isEdit && (v <= 0 || v - i.pph_remaining > 0.005);
                  return (
                    <tr key={key} className="border-b border-slate-100" data-testid={`pph-pay-row-${i.entry_id}`}>
                      <td className="px-2.5 py-1.5">
                        <div className="font-semibold text-slate-800">{i.invoice_no || "Belum ada nomor invoice"}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[260px]">{i.project_name}</div>
                      </td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums text-slate-600">
                        {formatIDR(isEdit ? (i.pph_remaining ?? i.amount) : remaining)}
                      </td>
                      <td className="px-2.5 py-1.5 text-right">
                        <input type="number" min="0" step="1000" value={allocs[key] ?? ""}
                          onChange={(e) => setAllocs((m) => ({ ...m, [key]: e.target.value }))}
                          data-testid={`pph-alloc-${i.entry_id}`}
                          className={`w-32 border px-1.5 py-1 text-[11px] text-right tabular-nums bg-white focus:outline-none ${bad ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-[#0B1D3A]"}`} />
                        {bad && <div className="text-[9px] text-rose-600 mt-0.5">Melebihi sisa / tidak valid</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50">
                  <td className="px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Total PPh tercakup</td>
                  <td />
                  <td className="px-2.5 py-1.5 text-right font-display font-bold text-[#0B1D3A] tabular-nums" data-testid="pph-pay-total">
                    {formatIDR(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-[10px] text-slate-400">
            Pencatatan ini hanya melacak bukti setor PPh — nilai invoice, penerimaan client, dan pembukuan di software akuntansi terpisah TIDAK berubah.
          </p>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button type="button" onClick={onClose} data-testid="pph-pay-cancel"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            {ACTION_LABEL.cancel}
          </button>
          <button type="button" onClick={save} disabled={busy || invalid || total <= 0} data-testid="pph-pay-save"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Menyimpan…" : (isEdit ? "Simpan Koreksi" : "Simpan Pembayaran")}
          </button>
        </div>
      </div>
    </div>
  );
}
